/*
 * Find My Size (Bedenimi Bul) — beden öneri hesaplayıcısı.
 *
 * Tüm ayarlar aşağıdaki FMS_CONFIG nesnesinde. Markadan gerçek ölçü tablosu
 * geldiğinde sadece bu nesneyi güncellemek yeterli; başka yere dokunmaya
 * gerek yok.
 *
 * Müşterinin ölçüleri yalnızca tarayıcısında saklanır (localStorage);
 * sunucuya gönderilmez. Hesaplama yapılmış bir üründe sepete eklemede
 * siparişe gizli "_Groessenempfehlung" notu düşülür — iade analizinde
 * önerilen beden ile satın alınan beden karşılaştırılabilir.
 */
const FMS_CONFIG = {
  // Beden merdiveni (küçükten büyüğe). Kesim tercihi ve boy düzeltmesi bu
  // merdiven üzerinde bir adım kaydırır.
  ladder: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],

  // BMI (kilo / boy² m²) üst sınırına göre beden eşleme, cinsiyete göre.
  // [üstSınır, beden] — ilk eşleşen aralık kullanılır.
  charts: {
    female: [
      [18, 'XS'],
      [20.5, 'S'],
      [23, 'M'],
      [25.5, 'L'],
      [28.5, 'XL'],
      [Infinity, 'XXL'],
    ],
    male: [
      [19.5, 'XS'],
      [21.5, 'S'],
      [24, 'M'],
      [26.5, 'L'],
      [29, 'XL'],
      [Infinity, 'XXL'],
    ],
    unisex: [
      [18.5, 'XS'],
      [21, 'S'],
      [23.5, 'M'],
      [26, 'L'],
      [28.5, 'XL'],
      [Infinity, 'XXL'],
    ],
  },

  // Bu boyun (cm) üzerindekilere bir beden büyük önerilir.
  tallHeight: { female: 175, male: 188, unisex: 182 },

  // Kesim tercihi: dar kesim sevenlere bir küçük, geniş sevenlere bir büyük.
  fitAdjust: { tight: -1, normal: 0, loose: 1 },

  // Üründeki bedenler numaraysa (36, 38…) harf bedenin karşılığı.
  // Alman konfeksiyon numaraları baz alındı.
  numericMap: {
    female: { XS: '34', S: '36', M: '38', L: '40', XL: '42', XXL: '44' },
    male: { XS: '44', S: '46', M: '48', L: '50', XL: '52', XXL: '54' },
    unisex: { XS: '34', S: '36', M: '38', L: '40', XL: '42', XXL: '44' },
  },

  // Pantolon bedenleri için harf bedenin bel (inç) karşılığı.
  // "30/32" formatında ilk sayı bel, ikinci sayı iç boy kabul edilir.
  waistMap: {
    female: { XS: 25, S: 26, M: 28, L: 30, XL: 32, XXL: 34 },
    male: { XS: 28, S: 30, M: 32, L: 34, XL: 36, XXL: 38 },
    unisex: { XS: 26, S: 28, M: 30, L: 32, XL: 34, XXL: 36 },
  },

  // Geçerli giriş aralıkları [min, max].
  limits: { weight: [30, 250], height: [120, 230] },

  // Ayakkabı: EU numara ≈ ayak uzunluğu (cm) × 1.5 + 1.5.
  // footToHeightRatio yalnızca emniyet ağı için: ayakkabı tespit edilemeyip
  // kilo/boy formu gösterildiyse ayak uzunluğu boydan tahmin edilir.
  shoe: {
    factor: 1.5,
    offset: 1.5,
    footLimits: [18, 35],
    footToHeightRatio: 0.152,
  },

  // Ölçülerin saklandığı localStorage anahtarı ve siparişe düşen gizli
  // notun adı (baştaki _ müşteriye gösterilmemesini sağlar).
  storageKey: 'campione_fms_profile',
  orderProperty: '_Groessenempfehlung',
};

if (!customElements.get('size-finder')) {
  customElements.define(
    'size-finder',
    class SizeFinder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.weightInput = this.querySelector('[data-fms-weight]');
        this.heightInput = this.querySelector('[data-fms-height]');
        this.footInput = this.querySelector('[data-fms-foot]');
        this.errorEl = this.querySelector('[data-fms-error]');
        this.resultEl = this.querySelector('[data-fms-result]');
        this.resultSizeEl = this.querySelector('[data-fms-result-size]');
        this.applyButton = this.querySelector('[data-fms-apply]');
        this.fit = 'normal';

        this.querySelectorAll('[data-fms-fit]').forEach((button) => {
          button.addEventListener('click', () => this.selectFit(button));
        });
        this.querySelector('[data-fms-calculate]').addEventListener('click', () => this.calculate());
        if (this.applyButton) {
          this.applyButton.addEventListener('click', () => this.applySize());
        }

        this.profile = this.loadProfile();
        this.prefill();
        this.autoRecommend();
      }

      selectFit(selected) {
        this.fit = selected.dataset.fmsFit;
        this.querySelectorAll('[data-fms-fit]').forEach((button) => {
          const isActive = button === selected;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-checked', isActive);
        });
      }

      calculate() {
        if (this.dataset.category === 'shoes') {
          this.calculateShoe();
          return;
        }

        const weight = parseFloat(this.weightInput.value);
        const height = parseFloat(this.heightInput.value);
        const { limits } = FMS_CONFIG;

        const valid =
          weight >= limits.weight[0] &&
          weight <= limits.weight[1] &&
          height >= limits.height[0] &&
          height <= limits.height[1];

        this.errorEl.hidden = valid;
        this.resultEl.hidden = true;
        if (!valid) return;

        this.saveProfile({ weight: weight, height: height, fit: this.fit });
        this.showResult(this.sizeFromMeasures(weight, height, this.fit));
      }

      calculateShoe() {
        const foot = parseFloat(this.footInput.value);
        const [min, max] = FMS_CONFIG.shoe.footLimits;
        const valid = foot >= min && foot <= max;

        this.errorEl.hidden = valid;
        this.resultEl.hidden = true;
        if (!valid) return;

        this.saveProfile({ foot: foot });
        this.showResult(this.shoeSizeFromFoot(foot));
      }

      // Kilo/boy/kesimden ürünün kendi formatında beden döndürür.
      sizeFromMeasures(weight, height, fit) {
        const gender = this.dataset.gender || 'unisex';
        const chart = FMS_CONFIG.charts[gender] || FMS_CONFIG.charts.unisex;
        const bmi = weight / Math.pow(height / 100, 2);

        let letter = chart[chart.length - 1][1];
        for (const [maxBmi, size] of chart) {
          if (bmi < maxBmi) {
            letter = size;
            break;
          }
        }

        let index = FMS_CONFIG.ladder.indexOf(letter);
        if (height >= (FMS_CONFIG.tallHeight[gender] || FMS_CONFIG.tallHeight.unisex)) index += 1;
        index += FMS_CONFIG.fitAdjust[fit] || 0;
        index = Math.min(Math.max(index, 0), FMS_CONFIG.ladder.length - 1);

        return this.displaySize(FMS_CONFIG.ladder[index], gender, height);
      }

      // Ayak uzunluğundan (cm) EU numara; üründe satılan numaralardan en
      // yakını seçilir, beden listesi yoksa yuvarlanmış numara gösterilir.
      shoeSizeFromFoot(footCm) {
        const eu = footCm * FMS_CONFIG.shoe.factor + FMS_CONFIG.shoe.offset;
        const sizes = this.sizeValues()
          .map((value) => parseFloat(value))
          .filter((value) => !Number.isNaN(value));
        return String(sizes.length ? this.nearest(sizes, eu) : Math.round(eu));
      }

      // Harf bedeni ürünün kendi beden formatına çevirir: numara (36, 38…)
      // veya pantolon bel/boy formatı (30/32).
      displaySize(letter, gender, height) {
        const sizes = this.sizeValues();
        if (sizes.length === 0) return letter;

        if (sizes.every((value) => /^\d{2,3}\s*\/\s*\d{2,3}$/.test(value))) {
          return this.pantsSize(letter, gender, height, sizes);
        }

        if (sizes.every((value) => /^\d+([.,]\d+)?$/.test(value))) {
          const values = sizes.map(parseFloat);
          // Alman konfeksiyon numaraları 32'den başlar; daha küçük değer
          // varsa bunlar bel ölçüsüdür (ör. jean 28, 30, 31…).
          if (Math.min(...values) < 32) {
            const waistMap = FMS_CONFIG.waistMap[gender] || FMS_CONFIG.waistMap.unisex;
            return String(this.nearest(values, waistMap[letter]));
          }
          // Emniyet ağı: konfeksiyon numaraları hep çifttir; 35-50 arasında
          // tek sayı varsa (41, 43…) bunlar ayakkabı numarasıdır ama ürün
          // ayakkabı olarak tespit edilememiştir. Numara boydan tahmin edilir.
          const hasOdd = values.some((value) => Number.isInteger(value) && value % 2 === 1);
          if (hasOdd && Math.min(...values) >= 35 && Math.max(...values) <= 50) {
            const foot = height * FMS_CONFIG.shoe.footToHeightRatio;
            const eu = foot * FMS_CONFIG.shoe.factor + FMS_CONFIG.shoe.offset;
            return String(this.nearest(values, eu));
          }
          const map = FMS_CONFIG.numericMap[gender] || FMS_CONFIG.numericMap.unisex;
          return map[letter] || letter;
        }

        return letter;
      }

      // "30/32" formatı: bel hedefi harf bedenden, iç boy hedefi boydan
      // (iç boy ≈ boyun %45'i, inç cinsinden). Üründe gerçekten var olan
      // bedenler arasından en yakını seçilir; bel uzunluktan önceliklidir.
      pantsSize(letter, gender, height, sizes) {
        const waistMap = FMS_CONFIG.waistMap[gender] || FMS_CONFIG.waistMap.unisex;
        const waistTarget = waistMap[letter];
        const lengthTarget = Math.round((height * 0.45) / 2.54);

        let best = null;
        let bestScore = Infinity;
        for (const value of sizes) {
          const [waist, length] = value.split('/').map((part) => parseFloat(part));
          const score = Math.abs(waist - waistTarget) * 10 + Math.abs(length - lengthTarget);
          if (score < bestScore) {
            bestScore = score;
            best = waist + '/' + length;
          }
        }
        return best || letter;
      }

      nearest(values, target) {
        return values.reduce((closest, value) =>
          Math.abs(value - target) < Math.abs(closest - target) ? value : closest
        );
      }

      sizeValues() {
        return (this.dataset.sizes || '')
          .split('|')
          .map((value) => value.trim())
          .filter(Boolean);
      }

      // ---- Sonuç gösterimi ve sayfa entegrasyonu ----

      showResult(size) {
        this.resultSizeEl.textContent = size;
        this.resultEl.hidden = false;
        this.applyValue = this.matchingVariantValue(size);
        if (this.applyButton) this.applyButton.hidden = !this.applyValue;
        this.publishRecommendation(size);
      }

      // Önerilen beden üründe gerçekten satılıyorsa varyant değerini döndürür.
      matchingVariantValue(size) {
        const normalized = String(size).replace(/\s+/g, '').toLowerCase();
        return (
          this.sizeValues().find((value) => value.replace(/\s+/g, '').toLowerCase() === normalized) || null
        );
      }

      // Sonuçtan tek tıkla bedeni varyant seçicide işaretler ve modalı kapatır.
      applySize() {
        if (this.applyValue) {
          const root = document.getElementById('variant-selects-' + this.dataset.sectionId) || document;
          const optionName = (this.dataset.sizeOption || '').toLowerCase();
          const normalized = this.applyValue.replace(/\s+/g, '').toLowerCase();
          const radio = Array.from(root.querySelectorAll('input[type="radio"]')).find(
            (input) =>
              (!optionName || input.name.toLowerCase().startsWith(optionName)) &&
              input.value.replace(/\s+/g, '').toLowerCase() === normalized
          );
          if (radio && !radio.checked) radio.click();
        }
        const modal = this.closest('modal-dialog');
        if (modal) modal.hide();
      }

      // Beden kutularının üstündeki "Önerimiz: L" yazısını günceller ve
      // siparişe düşecek gizli notu yazar.
      publishRecommendation(size) {
        document.querySelectorAll('[data-fms-recommended]').forEach((el) => {
          const sizeEl = el.querySelector('[data-fms-recommended-size]');
          if (sizeEl) sizeEl.textContent = size;
          el.hidden = false;
        });
        this.setOrderProperty(size);
      }

      setOrderProperty(size) {
        const form = document.getElementById('product-form-' + this.dataset.sectionId);
        if (!form) return;
        const name = 'properties[' + FMS_CONFIG.orderProperty + ']';
        let input = form.querySelector('input[name="' + name + '"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = size;
      }

      // ---- Ölçü hatırlama (yalnızca müşterinin tarayıcısında) ----

      loadProfile() {
        try {
          return JSON.parse(localStorage.getItem(FMS_CONFIG.storageKey)) || {};
        } catch (error) {
          return {};
        }
      }

      saveProfile(patch) {
        this.profile = Object.assign({}, this.profile, patch);
        try {
          localStorage.setItem(FMS_CONFIG.storageKey, JSON.stringify(this.profile));
        } catch (error) {
          // localStorage kapalıysa (gizli mod vb.) hatırlama sessizce devre dışı kalır
        }
      }

      prefill() {
        const profile = this.profile;
        if (this.footInput && profile.foot) this.footInput.value = profile.foot;
        if (this.weightInput && profile.weight) this.weightInput.value = profile.weight;
        if (this.heightInput && profile.height) this.heightInput.value = profile.height;
        if (profile.fit) {
          const button = this.querySelector('[data-fms-fit="' + profile.fit + '"]');
          if (button) this.selectFit(button);
        }
      }

      // Daha önce ölçü girilmişse sayfa açılır açılmaz bu ürün için öneri üretir.
      autoRecommend() {
        const profile = this.profile;
        let size = null;
        if (this.dataset.category === 'shoes') {
          if (profile.foot) size = this.shoeSizeFromFoot(profile.foot);
        } else if (profile.weight && profile.height) {
          size = this.sizeFromMeasures(profile.weight, profile.height, profile.fit || 'normal');
        }
        if (size) this.publishRecommendation(size);
      }
    }
  );
}
